import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { api } from '../services/api.js';

const SchoolYearContext = createContext(undefined);
const SchoolYearProvider = ({ children, studioGroup }) => {
    const today = new Date();
    const year = today.getMonth() < 8 ? today.getFullYear() - 1 : today.getFullYear();
    const defaultSchoolYear = {
        from: new Date(year, 8, 1, 0, 0, 0, 0),
        to: new Date(year + 1, 7, 31, 23, 59, 59, 999),
    };
    const [currentSchoolYear, setCurrentSchoolYear] = useState(defaultSchoolYear);
    const [schoolYears, setSchoolYears] = useState([]);
    useEffect(() => {
        async function fetchSchoolYears() {
            try {
                const response = await api.get(`api/studio-groups/${studioGroup}/school-years`);
                const schoolYears = response.data;
                const activeSchoolYear = schoolYears.find(schoolYear => schoolYear.isActive);
                if (!activeSchoolYear)
                    throw new Error('No active school year found');
                const sortedSchoolYears = schoolYears
                    .map(schoolYear => ({
                    from: new Date(schoolYear.startDate),
                    to: new Date(schoolYear.endDate),
                }))
                    .sort((a, b) => a.from.getTime() - b.from.getTime());
                setSchoolYears(sortedSchoolYears);
                setCurrentSchoolYear({
                    from: new Date(activeSchoolYear.startDate),
                    to: new Date(activeSchoolYear.endDate),
                });
            }
            catch (err) {
                throw err;
            }
        }
        fetchSchoolYears();
    }, [studioGroup]);
    const isPastSchoolYear = useCallback((schoolYear) => {
        return schoolYear.from.getFullYear() < currentSchoolYear.from.getFullYear();
    }, [currentSchoolYear]);
    const nextSchoolYear = useCallback(() => {
        const currentEnd = currentSchoolYear.to.getTime();
        return schoolYears.find(schoolYear => schoolYear.from.getTime() > currentEnd);
    }, [currentSchoolYear, schoolYears]);
    const previousSchoolYear = useCallback(() => {
        const currentStart = currentSchoolYear.from.getTime();
        const pastSchoolYears = schoolYears.filter(schoolYear => schoolYear.to.getTime() < currentStart);
        return pastSchoolYears.length > 0 ? pastSchoolYears[pastSchoolYears.length - 1] : undefined;
    }, [currentSchoolYear, schoolYears]);
    return (React.createElement(SchoolYearContext.Provider, { value: {
            currentSchoolYear,
            nextSchoolYear: nextSchoolYear(),
            previousSchoolYear: previousSchoolYear(),
            isPastSchoolYear,
            schoolYears,
        } }, children));
};
const useSchoolYear = () => {
    const context = useContext(SchoolYearContext);
    if (context === undefined) {
        throw new Error('useSchoolYear must be used within a SchoolYearProvider');
    }
    return context;
};

export { SchoolYearProvider, SchoolYearContext as default, useSchoolYear };
